/* istanbul ignore file */

import React, { useEffect } from 'react';
import { shape, string, func, bool } from 'prop-types';
import { supportedHubspotPrefillFields } from '../hubspotFormConfigs';

/**
 * A React component that renders and manages a HubSpot form.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.formConfig - Configuration object found in ../hubspotFormConfigs.js
 * @param {Function} [props.onReady=()=>{}] - Callback fn that is called when the form is ready
 *    e.g. updating a loading state
 * @param {boolean} [props.prefill=false] - Whether to attempt to prefill form fields with user data
 *    Fields found in ../hubspotFormConfigs.js
 * @param {Object} props.userContext
 * @param {Function} [props.onSubmit=()=>{}] - Callback fn that is called when the form is submitted
 *    e.g. closing the modal, submitting a segment event
 *
 * @returns {JSX.Element} A div container with the HubSpot form as an embedded iframe
 */
const HubspotForm = ({
  formConfig, onReady, prefill, userContext, onSubmit,
}) => {
  useEffect(() => {
    const formContainer = document.querySelector('#hubspot-form-container');

    if (formContainer && window.HubSpotForms) {
      // Create the form using HubSpot's forms API
      // https://developers.hubspot.com/docs/reference/cms/forms/legacy-forms
      window.HubSpotForms.create({
        portalId: formConfig.portalId,
        formId: formConfig.formId,
        target: '#hubspot-form-container',
        ...formConfig.additionalOptions,
        onFormReady: () => {
          // Prefill the form fields if applicable
          if (prefill) {
            const form = window.HubSpotForms.getForm(formConfig.formId);
            if (form) {
              Object.entries(supportedHubspotPrefillFields)
                .forEach(([fieldName, userContextProperty]) => {
                  form.setFieldValue(fieldName, userContext[userContextProperty]);
                });
            }
          }

          if (typeof onReady === 'function') {
            onReady();
          }
        },
        onFormSubmitted: () => {
          if (typeof onSubmit === 'function') {
            onSubmit();
          }
        },
      });
    }

    return () => {
      const cleanupContainer = document.getElementById('hubspot-form-container');
      if (cleanupContainer) {
        while (cleanupContainer.firstChild) {
          cleanupContainer.removeChild(cleanupContainer.firstChild);
        }
      }
    };
  }, []);

  return <div id="hubspot-form-container" />;
};

HubspotForm.propTypes = {
  formConfig: shape({
    portalId: string.isRequired,
    formId: string.isRequired,
    additionalOptions: shape({}),
  }),
  onReady: func,
  onSubmit: func,
  prefill: bool,
  userContext: shape({
    email: string.isRequired,
    firstName: string.isRequired,
    lastName: string,
  }).isRequired,
};

HubspotForm.defaultProps = {
  formConfig: {
    additionalOptions: {},
  },
  onReady: () => {},
  onSubmit: () => {},
  prefill: false,
};

export default HubspotForm;
